import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import apiUrl from "../Apiurl";
import DataTableBase from "../interfaccia/DataTableBase";
import stilePulsanti from "../interfaccia/stilePulsanti";
import BlockSkeleton from "../interfaccia/loading/BlockSkeleton";

const DEFAULT_STATS = {
  totale: 0,
  totale_web: 0,
  totale_moda: 0,
  con_stock: 0,
  disattivati_con_stock: 0,
  disattivati_con_stock_vig: 0,
  disattivati_con_stock_bot: 0,
  con_stock_pubblicati: 0,
  con_stock_non_pubblicati: 0,
  con_stock_problema: 0,
  pubblicati: 0,
  non_pubblicati: 0,
  problema: 0,
  non_pubblicare: 0,
  solo_img: 0,
  solo_testo: 0,
};

const prepareBrands = (list) =>
  (Array.isArray(list) ? list : []).map((item) => ({
    ...item,
    stats: {
      ...DEFAULT_STATS,
      ...(item.stats ?? {}),
      disattivati_con_stock_vig:
        item.stats?.disattivati_con_stock_vig ??
        item.stats?.disattivati_con_stock ??
        0,
      totale_web: item.stats?.totale_web ?? item.stats?.totale ?? 0,
    },
  }));

const buildCompletionData = (stats) => {
  const safeStats = stats ?? DEFAULT_STATS;
  const problemaCount =
    typeof safeStats.con_stock_problema === "number"
      ? safeStats.con_stock_problema
      : safeStats.problema ?? 0;
  const base = safeStats.con_stock ?? 0;
  const completati =
    (safeStats.pubblicati ?? 0) +
    (safeStats.non_pubblicare ?? 0) +
    problemaCount;
  const completion =
    base > 0 ? Math.round(Math.min(1, Math.max(0, completati / base)) * 100) : 0;
  const problemaPerc =
    base > 0 ? Math.round(Math.min(1, Math.max(0, problemaCount / base)) * 100) : 0;
  const greenPerc = Math.max(0, completion - problemaPerc);

  return {
    completion,
    problemaPerc,
    problemaCount,
    conStock: base,
  };
};

const buildCompletionBackground = (completionData) => {
  const greenEnd = Math.min(100, completionData.greenPerc ?? 0);
  const orangeEnd = Math.min(100, greenEnd + (completionData.problemaPerc ?? 0));
  return `linear-gradient(90deg,
    rgba(19,119,82,0.25) 0%,
    rgba(19,119,82,0.25) ${greenEnd}%,
    rgba(255,165,0,0.35) ${greenEnd}%,
    rgba(255,165,0,0.35) ${orangeEnd}%,
    rgba(255,65,54,0.1) ${orangeEnd}%,
    rgba(255,65,54,0.1) 100%)`;
};

const StatsPubblBrandPage = ({ token, utente, temaColori }) => {
  const { buttonClassesWhite } = stilePulsanti;
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [seasonOptions, setSeasonOptions] = useState([]);
  const [seasonOptionsError, setSeasonOptionsError] = useState("");
  const [selectedSeason, setSelectedSeason] = useState("");
  const [selectionFilters, setSelectionFilters] = useState({ stagione: "" });
  const [filterNoMP, setFilterNoMP] = useState(false);
  const [filterNoSito, setFilterNoSito] = useState(false);
  const [filterInventorySharing, setFilterInventorySharing] = useState(false);
  const [filterDropshipping, setFilterDropshipping] = useState(false);
  const [filterUnderPercent, setFilterUnderPercent] = useState("");
  const [includeZeroStock, setIncludeZeroStock] = useState(false);

  const canRead = Boolean(utente?.permessiAPI?.["read:database"]);

  useEffect(() => {
    if (!token || !canRead) {
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    setSeasonOptionsError("");
    axios.get(`${apiUrl}/moda/brand-settings/stagioni`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    }).then((response) => {
      if (!cancelled) {
        setSeasonOptions(Array.isArray(response?.data?.data) ? response.data.data : []);
      }
    }).catch((fetchError) => {
      if (!cancelled && !controller.signal.aborted) {
        setSeasonOptionsError("Impossibile recuperare le stagioni acquisto. Ricarica la pagina per riprovare.");
      }
    });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [token, canRead]);

  useEffect(() => {
    if (!token || !canRead) {
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    const fetchBrands = async () => {
      setLoading(true);
      setError("");
      setBrands([]);
      try {
        const response = await axios.get(`${apiUrl}/moda/brand-settings`, {
          params: selectionFilters.stagione ? { stagione: selectionFilters.stagione } : {},
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (cancelled) {
          return;
        }
        const payload = response?.data;
        const list = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
          ? payload
          : [];
        setBrands(prepareBrands(list));
      } catch (fetchError) {
        if (cancelled || controller.signal.aborted) {
          return;
        }
        console.error("Errore recupero brand settings:", fetchError);
        setError(
          fetchError?.response?.data?.error ??
            fetchError.message ??
            "Impossibile recuperare i brand"
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchBrands();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [token, canRead, selectionFilters]);

  const filteredBrands = useMemo(() => {
    const term = search.trim().toLowerCase();
    const threshold =
      filterUnderPercent === "" ? null : Number(filterUnderPercent);
    return brands.filter((brand) => {
      const conStockRaw = brand?.stats?.con_stock;
      const hasStock =
        typeof conStockRaw === "number"
          ? conStockRaw > 0
          : Number(conStockRaw ?? 0) > 0;
      if (!includeZeroStock && !hasStock) {
        return false;
      }
      if (term) {
        const name = (brand.brand_name || "").toLowerCase();
        const code = (brand.brand_code || "").toLowerCase();
        if (!name.includes(term) && !code.includes(term)) {
          return false;
        }
      }
      if (filterNoMP && Number(brand.permesso_mp ?? 1) !== 0) {
        return false;
      }
      if (filterNoSito && Number(brand.permesso_sito ?? 1) !== 0) {
        return false;
      }
      if (filterInventorySharing && Number(brand.inventory_sharing ?? 0) !== 1) {
        return false;
      }
      if (filterDropshipping && Number(brand.dropshipping ?? 0) !== 1) {
        return false;
      }
      if (threshold !== null && Number.isFinite(threshold) && threshold >= 0) {
        const { completion } = buildCompletionData(brand.stats);
        if (completion >= threshold) {
          return false;
        }
      }
      return true;
    });
  }, [
    brands,
    includeZeroStock,
    filterDropshipping,
    filterInventorySharing,
    filterNoMP,
    filterNoSito,
    filterUnderPercent,
    search,
  ]);

  if (!canRead) {
    return (
      <div className={`${temaColori} pa3`}>
        <div className="sfondoBiancoTema pa4 br3 shadow-4">
          <p className="ma0">
            Non hai i permessi per visualizzare questa pagina.
          </p>
        </div>
      </div>
    );
  }

  const columns = [
    {
      name: "Brand",
      sortable: true,
      minWidth: "240px",
      selector: (row) => row.brand_name || "",
      cell: (row) => (
        <div>
          <div className="b">{row.brand_name}</div>
          <div className="gray f7">COD: {row.brand_code}</div>
        </div>
      ),
    },
    {
      name: "Priorità PE",
      sortable: true,
      width: "110px",
      selector: (row) => row.priority_pe ?? 0,
      right: true,
    },
    {
      name: "Priorità AI",
      sortable: true,
      width: "110px",
      selector: (row) => row.priority_ai ?? 0,
      right: true,
    },
    {
      name: "Sito",
      sortable: true,
      width: "80px",
      selector: (row) => Number(row.permesso_sito ?? 0),
      cell: (row) => (
        <span
          className={`material-icons ${
            row.permesso_sito ? "dark-green" : "light-red"
          }`}
        >
          {row.permesso_sito ? "check_circle" : "cancel"}
        </span>
      ),
      center: true,
      compact: true
    },
    {
      name: "MP",
      sortable: true,
      width: "80px",
      selector: (row) => Number(row.permesso_mp ?? 0),
      cell: (row) => (
        <span
          className={`material-icons ${
            row.permesso_mp ? "dark-green" : "light-red"
          }`}
        >
          {row.permesso_mp ? "check_circle" : "cancel"}
        </span>
      ),
      center: true,
      compact: true
    },
    {
      name: "Estero",
      sortable: true,
      width: "90px",
      selector: (row) => Number(row.permesso_estero ?? 0),
      cell: (row) => (
        <span
          className={`material-icons ${
            row.permesso_estero ? "dark-green" : "light-red"
          }`}
        >
          {row.permesso_estero ? "check_circle" : "cancel"}
        </span>
      ),
      center: true,
      compact: true
    },
    {
      name: "I.S.",
      sortable: true,
      width: "80px",
      selector: (row) => Number(row.inventory_sharing ?? 0),
      cell: (row) => (
        <span
          className={`material-icons ${
            row.inventory_sharing ? "dark-green" : "light-silver"
          }`}
        >
          {row.inventory_sharing ? "check_circle" : "do_not_disturb_on"}
        </span>
      ),
      center: true,
      compact: true
    },
    {
      name: "Drop",
      sortable: true,
      width: "80px",
      selector: (row) => Number(row.dropshipping ?? 0),
      cell: (row) => (
        <span
          className={`material-icons ${
            row.dropshipping ? "dark-green" : "light-silver"
          }`}
        >
          {row.dropshipping ? "check_circle" : "do_not_disturb_on"}
        </span>
      ),
      center: true,
      compact: true
    },
    {
      name: "Tot. Articoli",
      sortable: true,
      width: "115px",
      selector: (row) => row.stats?.totale_web ?? row.stats?.totale ?? 0,
      right: true,
    },
    {
      name: "Tot. Moda>0",
      sortable: true,
      width: "115px",
      selector: (row) => row.stats?.totale_moda ?? 0,
      right: true,
    },
    {
      name: "Stock web>0",
      sortable: true,
      width: "90px",
      selector: (row) => row.stats?.con_stock ?? 0,
      right: true,
      grow: 0.9,
      compact: true,
      cell: (row) => (
        <span className="b">
          {row.stats?.con_stock ? row.stats.con_stock : ""}
        </span>
      ),
    },
    {
      name: (
        <span title="Articoli con stock web > 0 disattivi su vigliettisport.com">
          Disatt. VIG
        </span>
      ),
      sortable: true,
      width: "100px",
      selector: (row) => row.stats?.disattivati_con_stock_vig ?? 0,
      right: true,
      compact: true,
      cell: (row) => row.stats?.disattivati_con_stock_vig || "",
    },
    {
      name: (
        <span title="Articoli con stock web > 0 disattivi su botteroski.com">
          Disatt. BOT
        </span>
      ),
      sortable: true,
      width: "100px",
      selector: (row) => row.stats?.disattivati_con_stock_bot ?? 0,
      right: true,
      compact: true,
      cell: (row) => row.stats?.disattivati_con_stock_bot || "",
    },
    {
      name: "Pubbl.",
      sortable: true,
      width: "90px",
      selector: (row) => row.stats?.pubblicati ?? 0,
      right: true,
      compact: true,
      cell: (row) => (
        <span className="dark-green">
          {row.stats?.pubblicati ? row.stats.pubblicati : ""}
        </span>
      ),
    },
    {
      name: "Non pubbl.",
      sortable: true,
      width: "110px",
      selector: (row) => row.stats?.non_pubblicati ?? 0,
      right: true,
      compact: true,
      cell: (row) => (
        <span className="dark-red">
          {row.stats?.non_pubblicati ? row.stats.non_pubblicati : ""}
        </span>
      ),
    },
    {
      name: "Problema",
      sortable: true,
      width: "110px",
      selector: (row) =>
        row?.stats?.con_stock_problema ?? row?.stats?.problema ?? 0,
      right: true,
      cell: (row) => (
        <span className="orange">
          {row?.stats?.con_stock_problema
            ? row.stats.con_stock_problema
            : row?.stats?.problema
            ? row.stats.problema
            : ""}
        </span>
      ),
      compact: true
    },
    {
      name: "Da non fare",
      sortable: true,
      width: "120px",
      selector: (row) => row.stats?.non_pubblicare ?? 0,
      right: true,
      compact: true,
      cell: (row) =>
        row.stats?.non_pubblicare ? row.stats.non_pubblicare : "",
    },
    {
      name: "Solo img",
      sortable: true,
      width: "100px",
      selector: (row) => row.stats?.solo_img ?? 0,
      right: true,
      compact: true,
      cell: (row) => (row.stats?.solo_img ? row.stats.solo_img : ""),
    },
    {
      name: "Solo testo",
      sortable: true,
      width: "110px",
      selector: (row) => row.stats?.solo_testo ?? 0,
      right: true,
      compact: true,
      cell: (row) => (row.stats?.solo_testo ? row.stats.solo_testo : ""),
    },
    {
      name: "% compl. (problemi)",
      sortable: true,
      minWidth: "180px",
      selector: (row) => buildCompletionData(row.stats).completion,
      cell: (row) => {
        const completionData = buildCompletionData(row.stats);
        const background = buildCompletionBackground({
          ...completionData,
          greenPerc: Math.max(
            0,
            completionData.completion - completionData.problemaPerc
          ),
        });
        return (
          <div className="ba b--black-10 br2 pa1 w-100 tc" style={{ background }}>
            {`${completionData.completion}% (${completionData.problemaCount})`}
          </div>
        );
      },
    },
  ];

  return (
    <div className={`${temaColori} pa2 pa3-ns contentSubHeader`}>
      <div className="sfondoBiancoTema br3 shadow-4 pa3">
        <div className="flex justify-between items-center flex-wrap">
          <div className="mb3 mb0-ns">
            <h2 className="ma0">Stats pubbl brand</h2>
            <p className="ma0 gray f6">
              Statistiche pubblicazione per brand (metriche operative su stock
              web &gt; 0).
            </p>
          </div>
          <div className="flex items-center flex-wrap">
            <input
              type="search"
              className="input-reset ba b--black-20 br2 pa2 mr2 w5"
              placeholder="Cerca per brand o codice"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <input
              type="number"
              min={0}
              max={100}
              className="input-reset ba b--black-20 br2 pa2 mr2 w5"
              placeholder="Sotto % compl."
              value={filterUnderPercent}
              onChange={(event) => setFilterUnderPercent(event.target.value)}
            />
            <button
              type="button"
              className={buttonClassesWhite}
              onClick={() => {
                setSearch("");
                setSelectedSeason("");
                setSelectionFilters({ stagione: "" });
                setFilterNoMP(false);
                setFilterNoSito(false);
                setFilterInventorySharing(false);
                setFilterDropshipping(false);
                setFilterUnderPercent("");
                setIncludeZeroStock(false);
              }}
            >
              Reset filtri
            </button>
          </div>
        </div>

        <form
          className="flex items-end flex-wrap mt3"
          onSubmit={(event) => {
            event.preventDefault();
            setSelectionFilters({ stagione: selectedSeason });
          }}
        >
          <label className="mr2 mb2">
            <span className="db mb1">Stagione acquisto</span>
            <select
              className="pa2 ba b--black-20 br2"
              value={selectedSeason}
              onChange={(event) => setSelectedSeason(event.target.value)}
            >
              <option value="">Tutte le stagioni</option>
              {seasonOptions.map((season) => (
                <option key={season} value={season}>{season}</option>
              ))}
            </select>
          </label>
          <button type="submit" className={`${buttonClassesWhite} mb2 mr3`}>
            Cerca
          </button>
          <span className="f6 mb2" role="status">
            Stagione applicata: {selectionFilters.stagione || "Tutte le stagioni"}
          </span>
        </form>
        {seasonOptionsError && (
          <div className="mt2 dark-red" role="alert">{seasonOptionsError}</div>
        )}

        <div className="flex flex-wrap mt3">
          <label className="mr3 mb2 flex items-center">
            <input
              type="checkbox"
              className="mr2"
              checked={filterNoMP}
              onChange={(event) => setFilterNoMP(event.target.checked)}
            />
            No permessi marketplace
          </label>
          <label className="mr3 mb2 flex items-center">
            <input
              type="checkbox"
              className="mr2"
              checked={filterNoSito}
              onChange={(event) => setFilterNoSito(event.target.checked)}
            />
            No permessi sito
          </label>
          <label className="mr3 mb2 flex items-center">
            <input
              type="checkbox"
              className="mr2"
              checked={filterInventorySharing}
              onChange={(event) =>
                setFilterInventorySharing(event.target.checked)
              }
            />
            I.S.
          </label>
          <label className="mr3 mb2 flex items-center">
            <input
              type="checkbox"
              className="mr2"
              checked={filterDropshipping}
              onChange={(event) => setFilterDropshipping(event.target.checked)}
            />
            Drop
          </label>
          <label className="mr3 mb2 flex items-center">
            <input
              type="checkbox"
              className="mr2"
              checked={includeZeroStock}
              onChange={(event) => setIncludeZeroStock(event.target.checked)}
            />
            Anche brand senza articoli con stock web
          </label>
        </div>

        {error && (
          <div className="mt3 pa3 br2 bg-washed-red dark-red">{error}</div>
        )}

        <div className="mt3">
          <DataTableBase
            columns={columns}
            data={filteredBrands}
            progressPending={loading}
            progressComponent={<BlockSkeleton height={320} />}
            highlightOnHover
            pagination
            paginationPerPage={20}
            paginationRowsPerPageOptions={[20, 50, 100]}
            striped
            dense
            noHeader
          />
        </div>
      </div>
    </div>
  );
};

export default StatsPubblBrandPage;
